// src/utils/engravingUtils.js

/**
 * Limpa caracteres residuais de pontuação ou separadores nas pontas do texto
 */
export function cleanEngravingText(txt) {
  if (!txt) return "";
  return txt
    .replace(/^[\s/|:;—–-]+/, "")
    .replace(/[\s/|:;—–-]+$/, "")
    .trim();
}

/**
 * Limpa o rótulo da peça (ex: "Gravação Verso Medalha 1" -> "Verso Medalha 1")
 */
export function cleanLabel(lbl) {
  if (!lbl) return null;
  let clean = lbl.trim();
  // Remove prefixos redundantes comuns em e-commerces
  clean = clean
    .replace(/^(?:grava[çc][ãa]o\s+(?:d[ao]s?\s+)?|texto\s+(?:d[ao]\s+grava[çc][ãa]o\s+)?)/i, "")
    .trim();
  if (!clean) return "Gravação";
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

/**
 * Retorna classes CSS de badge conforme a quantidade de caracteres:
 * - Até 10: Neutro / Roxo
 * - 11 a 15: Alerta Âmbar / Amarelo (> 10)
 * - > 15: Alerta Vermelho (> 15)
 */
export function getEngravingBadgeStyle(count) {
  if (count > 15) {
    return {
      badgeClass: "bg-red-50 text-red-700 border-red-200 font-bold",
      labelClass: "text-red-600 font-extrabold",
      dotClass: "bg-red-500",
      status: "danger",
      statusText: "Acima de 15 carac."
    };
  }
  if (count > 10) {
    return {
      badgeClass: "bg-amber-50 text-amber-800 border-amber-300 font-semibold",
      labelClass: "text-amber-700 font-bold",
      dotClass: "bg-amber-500",
      status: "warning",
      statusText: "Acima de 10 carac."
    };
  }
  return {
    badgeClass: "bg-purple-50 text-purple-700 border-purple-200 font-medium",
    labelClass: "text-purple-700 font-bold",
    dotClass: "bg-purple-500",
    status: "normal",
    statusText: "Tamanho ideal"
  };
}

// Lista estrita de rótulos permitidos (NUNCA usar regex genérico de dois pontos para não quebrar versículos como 1Sm7:12 ou horários como 14:35!)
const KNOWN_LABEL_REGEX = /^(?:grava[çc][ãa]o\s+)?(?:verso\s+medalha\s*\d+|frente\s+medalha\s*\d+|medalha\s*\d+(?:\s*(?:verso|frente))?|frente|verso|lado\s*[ab]|alian[çc]a\s*(?:\d+|masculina|feminina|dele|dela|ele|ela|masc|fem)?|anel\s*(?:\d+|masculino|feminino|dele|dela|ele|ela|masc|fem)?|pe[çc]a\s*\d+|pingente\s*\d+|parte\s*\d+|nome(?:\s+do)?\s+pet|telefone|fone|whatsapp|celular|data(?:\s+de\s+nascimento)?)$/i;

/**
 * Analisa um pedaço de texto para extrair rótulo e valor limpo
 */
function parsePartLabelAndText(rawChunk) {
  const trimmed = (rawChunk || "").trim();
  if (!trimmed) return { label: null, text: "", count: 0 };

  // Procura padrão "[Rótulo][: / - —][Texto]"
  // onde o Rótulo DEVE bater estritamente com KNOWN_LABEL_REGEX
  const splitIndex = trimmed.search(/[:/—–-]/);
  if (splitIndex !== -1) {
    const candidateLabel = trimmed.slice(0, splitIndex).trim();
    const candidateContent = trimmed.slice(splitIndex + 1);

    if (KNOWN_LABEL_REGEX.test(candidateLabel)) {
      const text = cleanEngravingText(candidateContent);
      const label = cleanLabel(candidateLabel);
      return { label, text, count: text.length };
    }
  }

  // Se não bater com rótulo conhecido, o texto inteiro é conteúdo de gravação legítimo
  const text = cleanEngravingText(trimmed);
  return { label: null, text, count: text.length };
}


/**
 * Decompõe e interpreta o texto bruto da gravação identificando múltiplas partes:
 * - "Gravação Verso Medalha 1: SANTOS FC / Gravação Verso Medalha 2: PAULO"
 * - "Nome do Pet: Tony"
 * - "Medalha 1: 22.02.2026 ... // Medalha 2: 06.07.2021 ..."
 * - "Frente: ... // Verso: ..."
 * - Separadores //, |, quebras de linha \n ou " / "
 */
export function parseEngravingParts(rawText) {
  if (!rawText || typeof rawText !== "string") return [];
  const trimmed = rawText.trim();
  if (
    !trimmed ||
    trimmed === "ND" ||
    trimmed === "-" ||
    trimmed === "N/A" ||
    trimmed.toLowerCase().startsWith("sem grava")
  ) {
    return [];
  }

  // 1. Tenta dividir primeiro por separadores explícitos:
  // - "//"
  // - "|"
  // - quebra de linha "\n"
  // - " / " (barra isolada com espaços ao redor)
  const explicitSplitRegex = /\s*(?:\/{2,}|\|+|\n+|\s\/\s)\s*/;

  if (explicitSplitRegex.test(trimmed)) {
    const chunks = trimmed.split(explicitSplitRegex).map((s) => s.trim()).filter(Boolean);
    if (chunks.length > 1) {
      return chunks.map((chunk, idx) => {
        const parsed = parsePartLabelAndText(chunk);
        const style = getEngravingBadgeStyle(parsed.count);
        return {
          id: `part-${idx}`,
          label: parsed.label || `Parte ${idx + 1}`,
          text: parsed.text,
          count: parsed.count,
          ...style
        };
      });
    }
  }

  // 2. Se não encontrou delimitador explícito, tenta regex de palavras-chave no corpo
  const labelPattern = /(?:^|[\/\n|]+|\s\/\s)\s*((?:grava[çc][ãa]o\s+)?(?:verso\s+medalha\s*\d+|frente\s+medalha\s*\d+|medalha\s*\d+(?:\s*(?:verso|frente))?|frente|verso|lado\s*[ab]|alian[çc]a\s*\d+|anel\s*\d+|pe[çc]a\s*\d+|pingente\s*\d+|nome(?:\s+do\s+pet)?))\s*[:/—–-]*\s*(.*?)(?=(?:[\/\n|]+|\s\/\s)\s*(?:(?:grava[çc][ãa]o\s+)?(?:verso\s+medalha\s*\d+|frente\s+medalha\s*\d+|medalha\s*\d+|frente|verso|lado\s*[ab]|alian[çc]a\s*\d+|anel\s*\d+|pe[çc]a\s*\d+|pingente\s*\d+|nome(?:\s+do\s+pet)?))\s*[:/—–-]*|$)/gis;

  const matches = [...trimmed.matchAll(labelPattern)];
  if (matches.length >= 2 || (matches.length === 1 && matches[0][1])) {
    const parts = [];
    for (const match of matches) {
      const rawLabel = match[1];
      const rawContent = match[2];
      const text = cleanEngravingText(rawContent);
      const label = cleanLabel(rawLabel);

      if (text) {
        const style = getEngravingBadgeStyle(text.length);
        parts.push({
          id: `part-${parts.length}`,
          label,
          text,
          count: text.length,
          ...style
        });
      }
    }
    if (parts.length > 0) return parts;
  }

  // 3. Tenta parse simples de "Chave: Valor" em peça única (ex: "Nome do Pet: Tony")
  const singleParsed = parsePartLabelAndText(trimmed);
  if (singleParsed.label && singleParsed.text) {
    const style = getEngravingBadgeStyle(singleParsed.count);
    return [
      {
        id: "part-0",
        label: singleParsed.label,
        text: singleParsed.text,
        count: singleParsed.count,
        ...style
      }
    ];
  }

  // 4. Peça única sem rótulo
  const text = cleanEngravingText(trimmed);
  const style = getEngravingBadgeStyle(text.length);
  return [
    {
      id: "part-single",
      label: null,
      text,
      count: text.length,
      ...style
    }
  ];
}

/**
 * Reconstroi o texto unificado da gravação a partir das partes editadas
 */
export function reassembleEngraving(parts) {
  if (!parts || !Array.isArray(parts) || parts.length === 0) return "";
  return parts
    .map((p) => {
      const cleanT = (p.text || "").trim();
      if (!cleanT) return "";
      return p.label ? `${p.label}: ${cleanT}` : cleanT;
    })
    .filter(Boolean)
    .join(" // ");
}

/**
 * Utilitário de cópia para a área de transferência com suporte a fallback
 */
export async function copyToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    console.warn("Clipboard API falhou, tentando fallback", e);
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textarea);
    return successful;
  } catch (err) {
    console.error("Erro ao copiar texto:", err);
    return false;
  }
}
